/* eslint-disable @typescript-eslint/no-unused-vars */
import { SerializedData } from 'bernie-core';
import { Store } from 'bernie-plugin-mobx';
import { Logger, NOOP_LOGGER } from 'bernie-logger';
import { action, makeObservable, observable } from 'mobx';
import { SanityApiSource } from '../sources';
import { checkValue } from 'src/components/functions';
import SanityApiSourceSingleton from './helpers/sanityApiSourceSingleton';
import {
  Batch1TemplateService,
  Batch2TemplateService,
  Batch3TemplateService,
  VisitUsaTemplateService,
  MagazineTemplateService,
  AllStudioTemplateService,
  ExpediaLiveTemplateService,
  OceanHotelService,
} from './sanity';
import { VrboBestTimeToBookService } from './sanity/vrboBestTimeToBookTemplateService';
import { PageTheme, FeedType } from 'src/components/common/module-util';
import getPageTags from './helpers/getTags';
import { extractText } from 'src/components/common/helper';

export class SanityStore extends Store {
  @observable public campaignName: string = undefined;
  @observable public sanityPageData: any = undefined;
  @observable public isHideThreeUp: boolean = false;
  @observable public templateParams: object = {};
  @observable public isGoGuides: boolean = false;

  private batch1TemplateService: Batch1TemplateService;
  private batch2TemplateService: Batch2TemplateService;
  private batch3TemplateService: Batch3TemplateService;
  private visitUsaTemplateService: VisitUsaTemplateService;
  private magazineTemplateService: MagazineTemplateService;
  private vrboBestTimeToBookService: VrboBestTimeToBookService;
  private allStudioTemplateService: AllStudioTemplateService;
  private expediaLiveTemplateService: ExpediaLiveTemplateService;
  private oceanHotelService: OceanHotelService;

  public constructor(
    private sanityApiSource: SanityApiSource,
    state: SerializedData = {},
    logger: Logger = NOOP_LOGGER,
  ) {
    super(state, logger);
    SanityApiSourceSingleton.setInstance(this.sanityApiSource);
    this.batch1TemplateService = new Batch1TemplateService();
    this.batch2TemplateService = new Batch2TemplateService();
    this.batch3TemplateService = new Batch3TemplateService();
    this.visitUsaTemplateService = new VisitUsaTemplateService();
    this.magazineTemplateService = new MagazineTemplateService();
    this.vrboBestTimeToBookService = new VrboBestTimeToBookService();
    this.allStudioTemplateService = new AllStudioTemplateService();
    this.expediaLiveTemplateService = new ExpediaLiveTemplateService();
    this.oceanHotelService = new OceanHotelService();
    makeObservable(this);
  }

  public hydrate(data: SerializedData): void {
    delete data.sanityApiSource;
    Object.assign(this, data);
  }

  // Define the setMcicd method
  public setMcicd(templateName: string, feedName: string): any {
    // Assuming mcicd is a function that returns some value
    let param1 = 'cplite';
    let param2 = templateName;
    if (checkValue(feedName)) {
      param1 = templateName;
      param2 = feedName;
    }

    return 'mcicid=cp.' + param1 + '.' + param2;
  }

  public setAdUnits(pageData): any {
    // Filter for ad units with placement 'Middle'
    const adUnitsMiddle = pageData?.content?.filter(
      (module) => module.module !== 'adUnit' || (module.module === 'adUnit' && module.layout !== 'Leaderboard'),
    );

    // Filter out ad units where placement is not 'Middle'
    const adUnitsNotMiddle = pageData?.content?.filter(
      (module) => module.module === 'adUnit' && module.layout === 'Leaderboard',
    );

    // Create the updated pageData object
    const updatedPageData = {
      ...pageData, // Keep other existing properties of pageData
      content: adUnitsMiddle, // Replace content with ad units that have 'Middle' placement
      adUnitsNotMiddle, // Add new property for ad units with placement other than 'Middle'
    };
    return updatedPageData; // Return the updated pageData
  }

  @action
  public async fetchSanityPageData(
    partner: string,
    templateName: string,
    feedName: string,
    pagelang: string,
    siteid: string,
    isCacheFeed: boolean,
    referrerId?: string,
    isDummyData?: boolean,
    batchQuery = 'b1',
    busaFilter?: string,
    brand?: string,
    isdraft?: string,
  ): Promise<any> {
    let pageData = undefined;

    if (this.sanityPageData === undefined) {
      // Logic for fetching data from Sanity
      // cmslite and spotlight are the only valid values for the batchQuery parameter
      const dataFetchServiceMatcher = {
        b1: () => this.batch1TemplateService.fetchAllBatch1Template(partner, templateName, isDummyData),
        b2: () => this.batch2TemplateService.fetchAllBatch2Template(partner, templateName, isDummyData),
        b3: () => this.batch3TemplateService.fetchAllBatch3Template(partner, templateName, isDummyData),
        busa: () =>
          checkValue(feedName)
            ? this.visitUsaTemplateService.fetchVisitUsaSubTemplate(feedName, pagelang, isDummyData, busaFilter)
            : this.visitUsaTemplateService.fetchVisitUsaHomeTemplate(partner, templateName, pagelang, isDummyData),
        magazine: () =>
          checkValue(feedName)
            ? this.magazineTemplateService.fetchMagazineArticleTemplate(
                partner,
                templateName,
                feedName,
                isDummyData,
                pagelang,
                brand,
                isdraft,
              )
            : this.magazineTemplateService.fetchMagazineHomeTemplate(
                partner,
                templateName,
                isDummyData,
                pagelang,
                brand,
                isdraft,
              ),
        btb: () =>
          this.vrboBestTimeToBookService.fetchVrboBestTimeToBookServiceTemplate(
            partner,
            templateName,
            feedName,
            isDummyData,
            pagelang,
            brand,
          ),
        all: () =>
          this.allStudioTemplateService.fetchAllStudioServiceTemplate(partner, templateName, isDummyData, pagelang),
        expediaLive: () =>
          this.expediaLiveTemplateService.featchExpediaLiveHome(
            partner,
            templateName,
            isDummyData,
            pagelang,
            brand,
            isdraft,
          ),
        oceanhotel: () =>
          checkValue(feedName)
            ? this.oceanHotelService.fetchSubTemplate(
                partner,
                templateName,
                feedName,
                isDummyData,
                pagelang,
                brand,
                isdraft,
              )
            : this.oceanHotelService.fetchHomeTemplate(partner, templateName, isDummyData, pagelang, brand, isdraft),
      };

      pageData = dataFetchServiceMatcher[batchQuery] ? await dataFetchServiceMatcher[batchQuery]() : [];

      if (Array.isArray(pageData) && pageData.length === 0) {
        pageData = 'campaign not found';
      } else {
        const mcicd = this.setMcicd(templateName, feedName);
        const pageDataWithAdUnits = this.setAdUnits(pageData);

        // Consolidating tracking object for clarity
        const tracking = {
          mcicid: mcicd,
          partner: partner,
          templateName: templateName,
          feedName: feedName,
          feedName2: '',
          pageLang: pagelang,
          batchQuery: batchQuery,
          siteid: siteid,
          isSanityHomePage: !checkValue(feedName),
          isDummyData: isDummyData,
        };

        this.sanityPageData = {
          ...pageDataWithAdUnits,
          tracking: tracking,
        };
      }
      this.campaignName = 'v3template';
      this.templateParams = {
        partner: partner,
        templateName: templateName,
        feedName: feedName,
        feedName2: '',
      };
    } else {
      pageData = this.sanityPageData;
    }

    return pageData;
  }

  @action
  public async hideThreeUpCard(isHideThreeUp: boolean): Promise<any> {
    this.isHideThreeUp = isHideThreeUp;
  }

  @action
  public async fetchCapsAPIHomePage(
    partner: string,
    templateName: string,
    feedName: string,
    pagelang: string,
    siteid: string,
    viewModelResponse?: any,
  ): Promise<any> {
    // Fetch data from the Sanity API
    let pageData = undefined;
    this.isGoGuides = true;
    if (this.sanityPageData === undefined) {
      this.campaignName = 'v3template';
      this.templateParams = { partner, feedName2: '' };

      let apiPageData = null;
      if(checkValue(viewModelResponse)){        
        apiPageData = viewModelResponse;
      }else{
        apiPageData = await this.sanityApiSource.fetchCapsAPIData(
          partner,
          templateName,
          feedName,
          pagelang,
          siteid,
        );
      }
      
      if (!checkValue(apiPageData) || apiPageData?.error) return [];

      const campaign = apiPageData?.pos;
      const heroTags = getPageTags(apiPageData?.tags?.tag);

      const {
        modules,
        heroChoice,
        heroSetting,
        sponsorHeroSettings,
        categoryLandingHeroSettings,
        hideMoreStories = false,
        ...rest
      } = apiPageData;

      const formattedNavigationData = this.formatNavigationData(apiPageData);

      const heroModule = this.getHeroModule(
        heroChoice,
        heroSetting,
        sponsorHeroSettings,
        categoryLandingHeroSettings,
        heroTags,
      );
      const formattedModules = modules.map((module) => ({
        module: module?._type,
        ...module,
        dataFeed: FeedType.Sanity,
      }));

      pageData = {
        ...rest,
        ...campaign,
        content: [heroModule, formattedNavigationData, ...formattedModules],
        isArticlePage: hideMoreStories,
        pageTheme: PageTheme.BrandLight,
        dataFeed: FeedType.Sanity,
        globalNavigationData: campaign || null,
        ignoreTextTruncate: true,
      };

      this.sanityPageData = {
        ...this.setAdUnits(pageData),
        tracking: {
          mcicid: this.setMcicd(templateName, feedName),
          partner,
          templateName,
          feedName,
          feedName2: '',
          pageLang: pagelang,
          siteid,
          isSanityHomePage: !checkValue(feedName),
        },
      };
    } else {
      pageData = this.sanityPageData;
    }
    return pageData;
  }

  private getHeroModule(
    heroChoice: string,
    heroSetting: any,
    sponsorHeroSettings: any,
    categoryLandingHeroSettings: any,
    heroTags: any,
  ) {
    if (heroChoice === 'Sponsored Hero' && checkValue(sponsorHeroSettings)) {
      return {
        module: sponsorHeroSettings._type,
        dataFeed: FeedType.Sanity,
        moduleType: 'hero',
        ...sponsorHeroSettings,
        tags: heroTags,
      };
    }
    if (heroChoice === 'Category Landing Hero' && checkValue(categoryLandingHeroSettings)) {
      return {
        module: categoryLandingHeroSettings._type,
        dataFeed: FeedType.Sanity,
        moduleType: 'hero',
        ...categoryLandingHeroSettings,
        tags: heroTags,
      };
    }
    if (checkValue(heroSetting)) {
      return {
        module: heroSetting._type,
        dataFeed: FeedType.Sanity,
        moduleType: 'hero',
        ...heroSetting,
        tags: heroTags,
      };
    }
    return null;
  }

  private formatNavigationData = ({ navigationTags, modules, navigationDropdownText, _type }) => {
    const baseNavData = {
      module: 'dynamicSubNav',
      dataFeed: FeedType.Sanity,
    };

    if (!checkValue(navigationTags, true))
      return {
        ...baseNavData,
        navL1: [],
        navL2: [],
        navigationDropdownText: '',
      };

    const moduleTitles = modules
      .flatMap((el) => {
        if (el._type === 'richTextEditor' && checkValue(el.bodyTextRaw, true)) {
          const h2Block = el.bodyTextRaw.find((block) => block.style === 'h2' && checkValue(block.children, true));
          const text = h2Block?.children?.[0]?.text;

          if (!checkValue(text)) return [];

          if (_type === 'ggListicleTemplate' && !/^\d+\.\s+/.test(text)) return [];

          return [{ title: text, url: extractText(text) }];
        }

        if (!checkValue(el?.title)) return [];

        if (_type === 'ggListicleTemplate' && !/^\d+\.\s+/.test(el.title)) return [];

        return [{ title: el.title, url: extractText(el.title) }];
      })
      .slice(0, 10);

    return {
      ...baseNavData,
      navL1: navigationTags,
      navL2: checkValue(moduleTitles, true) ? moduleTitles : [],
      navigationDropdownText,
    };
  };
}
